import {
  Box,
  Button,
  CircularProgress,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  handleSpringDataMutationRequest,
  handleSpringDataRequest,
} from "../../utils/apiUtils";
import { toast } from "react-toastify";
import AppsIcon from "@mui/icons-material/Apps";
import MemoryIcon from "@mui/icons-material/Memory";
import ExtensionIcon from "@mui/icons-material/Extension";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SecurityIcon from "@mui/icons-material/Security";
import BusinessIcon from "@mui/icons-material/Business";
import HubIcon from "@mui/icons-material/Hub";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import InventoryIcon from "@mui/icons-material/Inventory";
import ChatIcon from "@mui/icons-material/Chat";
import StarIcon from "@mui/icons-material/Star";
import LanguageIcon from "@mui/icons-material/Language";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ForumIcon from "@mui/icons-material/Forum";
import RateReviewIcon from "@mui/icons-material/RateReview";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import SyncIcon from "@mui/icons-material/Sync";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import "./styles/ModuleRegistry.css";

const CATEGORIES = [
  { id: "all", label: "All Modules", icon: AppsIcon },
  { id: "core", label: "Core Module", icon: MemoryIcon },
  { id: "addon", label: "Add-on Products", icon: ExtensionIcon },
  { id: "ai", label: "FLEXI AI Intelligence", icon: AutoAwesomeIcon },
  { id: "enterprise", label: "Enterprise Analytics Suite", icon: SecurityIcon },
];

const PARENT_CATEGORY = {
  pms: "core",
  channel_manager: "core",
  addons: "addon",
  ai_products: "ai",
};

const CORE_PARENT_KEYS = new Set(["pms", "channel_manager"]);
const ADDON_PARENT_KEYS = new Set(["addons"]);
const AI_PARENT_KEYS = new Set(["ai_products"]);
const ENTERPRISE_SUB_MODULES = new Set(["gha", "group dashboard"]);

const MODULE_ICONS = {
  pms: BusinessIcon,
  cm: HubIcon,
  "channel manager": HubIcon,
  pos: RestaurantIcon,
  "direct billing": ReceiptLongIcon,
  "stock management": InventoryIcon,
  "reputation management": StarIcon,
  "booking engine": LanguageIcon,
  "revenue management": TrendingUpIcon,
  "flexi ai": SmartToyIcon,
  "ai agent": SmartToyIcon,
  "ai message": ForumIcon,
  "ai review": RateReviewIcon,
  folio: ReceiptLongIcon,
  "reservation calendar": CalendarMonthIcon,
  reports: AssessmentIcon,
  housekeeping: CleaningServicesIcon,
  "channel mapping": SyncIcon,
  "inventory calendar": InventoryIcon,
  "unified inbox": ChatIcon,
  "expense manager": ReceiptLongIcon,
  gha: LanguageIcon,
  "group dashboard": AssessmentIcon,
  "night audit": AssessmentIcon,
  restrictions: SecurityIcon,
  "magic link and kiosk": LanguageIcon,
  "rental spaces": BusinessIcon,
  dashboard: AssessmentIcon,
  "property configuration": BusinessIcon,
};

const MODULE_TAGLINES = {
  pms: "Property Management System",
  "channel manager": "Channel Manager Gateway",
  channel_manager: "Channel Manager Gateway",
  cm: "Channel Manager Gateway",
  pos: "Restaurant & Outlet Management",
  "direct billing": "Corporate & B2B Billing",
  "stock management": "Inventory & Procurement",
  payments: "Payment Gateway Integration",
  messages: "Guest Communication Hub",
  "reputation management": "Review & Rating Management",
  "booking engine": "Direct Booking Website",
  "revenue management": "Dynamic Pricing Engine",
  "flexi ai": "Enterprise AI Layer",
  "ai agent": "Operational AI Assistant",
  "ai message": "AI-Powered Guest Messaging",
  "ai review": "Automated Review Responses",
};

const normalizeName = (name) =>
  name?.toLowerCase().replace(/[-_]/g, " ").trim() ?? "";

const normalizeKey = (name) =>
  name?.toLowerCase().replace(/-/g, "_").trim() ?? "";

const normalizeParentKey = (name) => normalizeKey(name);

const getDisplayParentName = (name) => {
  const key = normalizeParentKey(name);
  if (key === "pms") return "PMS";
  if (key === "channel_manager") return "CM";
  return name;
};

const usesParentApi = (parentName) =>
  CORE_PARENT_KEYS.has(normalizeParentKey(parentName));

const getModuleIcon = (name) => {
  const key = normalizeName(name);
  return MODULE_ICONS[key] || ExtensionIcon;
};

const getParentCategory = (parentName) =>
  PARENT_CATEGORY[normalizeParentKey(parentName)] || "addon";

const isCoreParent = (parentName) =>
  CORE_PARENT_KEYS.has(normalizeParentKey(parentName));

const extractSubModules = (node) => node?.sub_modules || [];

const featureModulesToMap = (data) => {
  if (!data) return {};
  if (Array.isArray(data)) {
    return data.reduce((acc, item) => {
      acc[item.module_name] = item;
      return acc;
    }, {});
  }
  return data;
};

const extractApiList = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.message)) return data.message;
  return [];
};

const normalizeSubModule = (sub, featureModulesMap = {}) => {
  const name = sub.sub_module_name || sub.module_name;
  const id = sub.sub_module_id ?? sub.id;
  const meta = featureModulesMap[name] || {};
  return {
    id,
    sub_module_id: id,
    module_name: name,
    module_description:
      meta.module_description || sub.module_description || name,
    parent_module_id: sub.parent_module_id ?? meta.parent_module_id,
    access_type: sub.access_type,
    features: sub.features || [],
  };
};

const flattenHierarchyTree = (tree) => {
  if (!tree) return [];
  if (Array.isArray(tree) && tree.length === 1 && Array.isArray(tree[0])) {
    return tree[0];
  }
  return Array.isArray(tree) ? tree : [];
};

const buildHierarchyFromFlat = (globalModules, featureModulesList) => {
  const parents = globalModules || [];
  const children = featureModulesList || [];
  return parents.map((parent) => ({
    id: parent.id,
    parent_module_id: parent.id,
    module_name: parent.module_name,
    module_description: parent.module_description,
    sub_modules: children
      .filter((child) => child.parent_module_id === parent.id)
      .map((child) => normalizeSubModule(child)),
  }));
};

const mergeHierarchySources = (tree, globalModules, featureModules) => {
  const featureMap = featureModulesToMap(featureModules);
  const flatTree = flattenHierarchyTree(tree);

  if (flatTree.length) {
    const globalById = {};
    (globalModules || []).forEach((module) => {
      globalById[module.id] = module;
    });

    return flatTree.map((parent) => {
      const global = globalById[parent.parent_module_id] || {};
      return {
        id: parent.parent_module_id,
        parent_module_id: parent.parent_module_id,
        module_name: parent.module_name || global.module_name,
        module_description:
          global.module_description || parent.module_description,
        sub_modules: extractSubModules(parent).map((sub) =>
          normalizeSubModule(sub, featureMap)
        ),
      };
    });
  }

  return buildHierarchyFromFlat(
    globalModules,
    Object.values(featureMap)
  );
};

const enrichHierarchyWithStatusIds = (hierarchy, subStatus, featureMap) => {
  const statusByName = {};
  (subStatus || []).forEach((item) => {
    statusByName[normalizeKey(item.module_name)] = item;
  });

  return hierarchy.map((parent) => ({
    ...parent,
    sub_modules: extractSubModules(parent).map((sub) => {
      const status = statusByName[normalizeKey(sub.module_name)];
      const meta = featureMap[sub.module_name] || {};
      const canonicalId = status?.id ?? sub.sub_module_id ?? sub.id ?? meta.id;
      return {
        ...sub,
        id: canonicalId,
        sub_module_id: canonicalId,
      };
    }),
  }));
};

const buildSubModuleCatalog = (subStatus, hierarchy, featureMap) => {
  const hierarchySubByName = {};
  hierarchy.forEach((parent) => {
    extractSubModules(parent).forEach((sub) => {
      hierarchySubByName[normalizeKey(sub.module_name)] = sub;
    });
  });

  return (subStatus || []).map((statusItem) => {
    const hierarchySub = hierarchySubByName[normalizeKey(statusItem.module_name)];
    const meta = featureMap[statusItem.module_name] || {};
    return {
      id: statusItem.id,
      module_name: statusItem.module_name,
      parent_module_id: statusItem.parent_module_id,
      module_description:
        hierarchySub?.module_description ||
        meta.module_description ||
        statusItem.module_name,
      features: hierarchySub?.features || [],
      access_type: hierarchySub?.access_type,
    };
  });
};

const getDependencyLabel = (subModule, parentById) => {
  const desc = (subModule.module_description || "").toLowerCase();
  if (desc.includes("requires pms or cm")) return "Requires PMS or CM";
  if (desc.includes("requires messages") || desc.includes("requires unified inbox")) {
    return "Requires Unified Inbox";
  }
  if (desc.includes("reputation")) return "Requires Reputation Management";

  const parent = parentById[subModule.parent_module_id];
  const parentKey = normalizeParentKey(parent?.module_name);

  if (ADDON_PARENT_KEYS.has(parentKey)) return "Requires PMS";
  if (AI_PARENT_KEYS.has(parentKey)) {
    const name = normalizeName(subModule.module_name);
    if (name === "ai agent") return "Requires PMS or CM";
    if (name === "ai message") return "Requires Unified Inbox";
    if (name === "ai review") return "Requires Reputation Management";
  }

  return null;
};

const isDependencyMet = (label, parentEnabledMap, subEnabledMap) => {
  if (!label) return true;
  const lower = label.toLowerCase();
  if (lower.includes("pms or cm")) {
    return parentEnabledMap.pms || parentEnabledMap.channel_manager;
  }
  if (lower.includes("reputation")) {
    return !!subEnabledMap["reputation management"];
  }
  if (lower.includes("unified inbox") || lower.includes("messages")) {
    return !!subEnabledMap["unified inbox"];
  }
  if (lower.includes("pms")) return !!parentEnabledMap.pms;
  if (lower.includes("cm") || lower.includes("channel")) {
    return !!parentEnabledMap.channel_manager;
  }
  return true;
};

function ModuleCard({
  badge,
  badgeClass,
  enabled,
  enabledClass,
  icon: Icon,
  title,
  tagline,
  isNew,
  description,
  dependency,
  dependencyMet,
  sectionLabel,
  chips,
  footerLabel,
  onToggle,
  toggleDisabled,
  toggleTooltip,
}) {
  return (
    <div className={`module-card ${enabled ? enabledClass : ""}`}>
      <div className="module-card__top">
        <span className={`module-card__badge module-card__badge--${badgeClass}`}>
          {badge}
        </span>
        <span
          className={`module-card__status ${
            enabled ? "module-card__status--on" : "module-card__status--off"
          }`}
        >
          <span className="module-card__status-dot" />
          {enabled ? "ENABLED" : "DISABLED"}
        </span>
      </div>

      <div className="module-card__head">
        <div className="module-card__icon">
          <Icon fontSize="small" />
        </div>
        <div>
          <div className="module-card__name-row">
            <h3 className="module-card__name">{title}</h3>
            {isNew && <span className="module-card__new">NEW</span>}
          </div>
          {tagline && <p className="module-card__tagline">{tagline}</p>}
        </div>
      </div>

      {description && <p className="module-card__desc">{description}</p>}

      {dependency && (
        <div className="module-card__dependency">
          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          <span>{dependency}</span>
          {!dependencyMet && (
            <span style={{ marginLeft: 4, fontWeight: 600 }}>(not met)</span>
          )}
        </div>
      )}

      {chips?.length > 0 && (
        <div>
          <p className="module-card__section-label">{sectionLabel}</p>
          <div className="module-card__chips">{chips}</div>
        </div>
      )}

      <div className="module-card__footer">
        <span className="module-card__footer-label">{footerLabel}</span>
        <Tooltip title={toggleTooltip || ""}>
          <span>
            <Switch
              checked={enabled}
              disabled={toggleDisabled}
              onChange={onToggle}
              color={badgeClass === "core" ? "primary" : "success"}
            />
          </span>
        </Tooltip>
      </div>
    </div>
  );
}

function TopologyGraph({ nodes }) {
  const columns = [
    { id: "core", title: "1. CORE MODULES", accent: "blue" },
    { id: "pipeline", title: "2. AUTO ENABLED", accent: "blue" },
    { id: "modules", title: "3. MODULES", accent: "green" },
    { id: "ai", title: "4. SMART LOGIC", accent: "purple" },
    { id: "enterprise", title: "5. GLOBAL SCALE", accent: "amber" },
  ];

  return (
    <div className="module-registry__topology">
      <div className="module-registry__topology-header">
        <div>
          <h3 className="module-registry__title">Enabled Modules</h3>
          <p className="module-registry__subtitle">
            Live synchronized structure representing real-time system configuration
            flows and relational bindings.
          </p>
        </div>
        <div className="module-registry__legend">
          <span className="module-registry__legend-item">
            <span
              className="module-registry__legend-dot"
              style={{ background: "#2563eb" }}
            />
            HOST CORE
          </span>
          <span className="module-registry__legend-item">
            <span
              className="module-registry__legend-dot"
              style={{ background: "#16a34a" }}
            />
            ADD-ON
          </span>
          <span className="module-registry__legend-item">
            <span
              className="module-registry__legend-dot"
              style={{ background: "#7c3aed" }}
            />
            AI MODEL
          </span>
          <span className="module-registry__legend-item">
            <span
              className="module-registry__legend-dot"
              style={{ background: "#d97706" }}
            />
            ENTERPRISE
          </span>
          <span className="module-registry__legend-item">
            <span
              className="module-registry__legend-dot"
              style={{ background: "#0d9488" }}
            />
            SYSTEM ADS
          </span>
        </div>
      </div>

      <div className="module-registry__topology-columns">
        {columns.map((column) => (
          <div key={column.id} className="topology-column">
            <p className="topology-column__title">{column.title}</p>
            {(nodes[column.id] || []).map((node) => {
              const Icon = node.icon;
              const activeClass = node.active
                ? `topology-node--active-${node.accent || column.accent}`
                : "topology-node--inactive";
              return (
                <div
                  key={node.id}
                  className={`topology-node ${activeClass}`}
                >
                  <Icon className="topology-node__icon" fontSize="inherit" />
                  <div className="topology-node__body">
                    <div className="topology-node__name">{node.title}</div>
                    <div className="topology-node__sub">{node.subtitle}</div>
                  </div>
                  {node.active && (
                    <CheckCircleIcon
                      sx={{ fontSize: 14, color: "#16a34a", ml: "auto" }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <p className="module-registry__topology-note">
        Topology maps represent functional product routing. Active glows denote
        modules that are fully authorized and successfully running on stay
        metadata.
      </p>
    </div>
  );
}

export default function Features({ hotelId, setRefresh }) {
  const [hierarchy, setHierarchy] = useState([]);
  const [subModuleCatalog, setSubModuleCatalog] = useState([]);
  const [parentEnabled, setParentEnabled] = useState({});
  const [subEnabled, setSubEnabled] = useState({});
  const [initialParentEnabled, setInitialParentEnabled] = useState({});
  const [initialSubEnabled, setInitialSubEnabled] = useState({});
  const [activeCategory, setActiveCategory] = useState("all");
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);

  const parentById = useMemo(() => {
    const map = {};
    hierarchy.forEach((parent) => {
      map[parent.id] = parent;
    });
    return map;
  }, [hierarchy]);

  const parentEnabledMap = useMemo(() => {
    const map = {};
    Object.entries(parentEnabled).forEach(([key, enabled]) => {
      map[key] = enabled;
    });
    return map;
  }, [parentEnabled]);

  const subEnabledMap = useMemo(() => {
    const map = {};
    Object.entries(subEnabled).forEach(([name, enabled]) => {
      map[normalizeName(name)] = enabled;
    });
    return map;
  }, [subEnabled]);

  const fetchModules = useCallback(() => {
    setFetching(true);
    Promise.all([
      handleSpringDataRequest(
        "core/api/v1/accesscontrol/get-module-hierarchy-tree"
      ).catch(() => null),
      handleSpringDataRequest(
        "core/api/v1/accesscontrol/get-all-global-modules"
      ),
      handleSpringDataRequest(
        "core/api/v1/accesscontrol/get-all-global-feature-modules"
      ).catch(() => []),
      handleSpringDataRequest(
        `core/api/v1/accesscontrol/get-hotel-parent-module-status?hotelId=${hotelId}`
      ),
      handleSpringDataRequest(
        `core/api/v1/accesscontrol/get-hotel-sub-module-status?hotelId=${hotelId}`
      ).catch(() => []),
    ])
      .then(([tree, globalModules, featureModules, parentStatus, subStatus]) => {
        const parentStatusList = extractApiList(parentStatus);
        const subStatusList = extractApiList(subStatus);
        const featureMap = featureModulesToMap(featureModules);
        const mergedHierarchy = mergeHierarchySources(
          tree,
          globalModules,
          featureModules
        );
        const enrichedHierarchy = enrichHierarchyWithStatusIds(
          mergedHierarchy,
          subStatusList,
          featureMap
        );
        const catalog = buildSubModuleCatalog(
          subStatusList,
          enrichedHierarchy,
          featureMap
        );

        const nextParentEnabled = {};
        (globalModules || []).forEach((module) => {
          if (usesParentApi(module.module_name)) {
            nextParentEnabled[normalizeParentKey(module.module_name)] = false;
          }
        });
        parentStatusList.forEach((item) => {
          if (usesParentApi(item.module_name)) {
            nextParentEnabled[normalizeParentKey(item.module_name)] =
              !!item.enabled;
          }
        });

        const nextSubEnabled = {};
        catalog.forEach((item) => {
          nextSubEnabled[normalizeKey(item.module_name)] = false;
        });
        subStatusList.forEach((item) => {
          nextSubEnabled[normalizeKey(item.module_name)] = !!item.enabled;
        });

        enrichedHierarchy.forEach((parent) => {
          if (!isCoreParent(parent.module_name)) return;
          const parentKey = normalizeParentKey(parent.module_name);
          const parentOn = nextParentEnabled[parentKey] ?? false;
          extractSubModules(parent).forEach((sub) => {
            nextSubEnabled[normalizeKey(sub.module_name)] = parentOn;
          });
        });

        setHierarchy(enrichedHierarchy);
        setSubModuleCatalog(catalog);
        setParentEnabled(nextParentEnabled);
        setSubEnabled(nextSubEnabled);
        setInitialParentEnabled({ ...nextParentEnabled });
        setInitialSubEnabled({ ...nextSubEnabled });
      })
      .catch(() => {
        toast.error("Failed to load modules");
      })
      .finally(() => {
        setFetching(false);
      });
  }, [hotelId]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const toggleParent = (parentName) => {
    if (!usesParentApi(parentName)) return;

    const key = normalizeParentKey(parentName);
    const turningOn = !parentEnabled[key];

    setParentEnabled((prev) => ({ ...prev, [key]: turningOn }));

    const parent = hierarchy.find(
      (item) => normalizeParentKey(item.module_name) === key
    );
    if (parent && isCoreParent(parent.module_name)) {
      setSubEnabled((prevSub) => {
        const updated = { ...prevSub };
        extractSubModules(parent).forEach((sub) => {
          updated[normalizeKey(sub.module_name)] = turningOn;
        });
        return updated;
      });
    }

    if (key === "pms" && !turningOn) {
      setSubEnabled((prevSub) => {
        const updated = { ...prevSub };
        hierarchy.forEach((parent) => {
          const parentKey = normalizeParentKey(parent.module_name);
          if (
            ADDON_PARENT_KEYS.has(parentKey) ||
            AI_PARENT_KEYS.has(parentKey)
          ) {
            extractSubModules(parent).forEach((sub) => {
              updated[normalizeKey(sub.module_name)] = false;
            });
          }
        });
        return updated;
      });
    }
  };

  const toggleSubModule = (subModule, parentModule) => {
    const key = normalizeKey(subModule.module_name);
    const dependency = getDependencyLabel(
      {
        ...subModule,
        parent_module_id: subModule.parent_module_id || parentModule?.id,
      },
      parentById
    );

    setSubEnabled((prev) => {
      const turningOn = !prev[key];
      if (
        turningOn &&
        dependency &&
        !isDependencyMet(dependency, parentEnabledMap, subEnabledMap)
      ) {
        toast.warning(dependency);
        return prev;
      }
      return { ...prev, [key]: turningOn };
    });
  };

  const displayCards = useMemo(() => {
    const cards = [];

    hierarchy.forEach((parent) => {
      const parentKey = normalizeParentKey(parent.module_name);
      const category = getParentCategory(parent.module_name);
      const subModules = extractSubModules(parent);
      const parentOn = parentEnabled[parentKey] ?? false;

      if (isCoreParent(parent.module_name)) {
        const Icon = getModuleIcon(parent.module_name);
        const chips = subModules.map((sub) => {
          const subKey = normalizeKey(sub.module_name);
          return (
            <span
              key={sub.sub_module_id || subKey}
              className={`module-chip ${
                parentOn ? "module-chip--on" : "module-chip--off"
              }`}
            >
              {sub.module_name}
            </span>
          );
        });

        cards.push({
          id: `parent-${parent.id || parentKey}`,
          category,
          node: (
            <ModuleCard
              key={`parent-${parent.id || parentKey}`}
              badge="CORE"
              badgeClass="core"
              enabled={parentOn}
              enabledClass="module-card--enabled-core"
              icon={Icon}
              title={getDisplayParentName(parent.module_name)}
              tagline={parent.module_description || MODULE_TAGLINES[parentKey]}
              description={parent.module_description}
              sectionLabel="BUNDLED SERVICES"
              chips={chips}
              footerLabel="HOST CORE"
              onToggle={() => toggleParent(parent.module_name)}
            />
          ),
        });
        return;
      }

      subModules.forEach((sub) => {
        const subKey = normalizeKey(sub.module_name);
        const subOn = subEnabled[subKey] ?? false;
        const dependency = getDependencyLabel(
          { ...sub, parent_module_id: sub.parent_module_id || parent.id },
          parentById
        );
        const dependencyMet = isDependencyMet(
          dependency,
          parentEnabledMap,
          subEnabledMap
        );
        const capabilities = (sub.features || [])
          .map((f) => f.feature_name)
          .filter(Boolean)
          .slice(0, 6);

        const subCategory = ENTERPRISE_SUB_MODULES.has(normalizeName(sub.module_name))
          ? "enterprise"
          : category;
        const badgeClass =
          subCategory === "ai"
            ? "ai"
            : subCategory === "enterprise"
            ? "enterprise"
            : "addon";

        cards.push({
          id: `sub-${sub.sub_module_id || subKey}`,
          category: subCategory,
          node: (
            <ModuleCard
              key={`sub-${sub.sub_module_id || subKey}`}
              badge={
                badgeClass === "ai"
                  ? "AI"
                  : badgeClass === "enterprise"
                  ? "ENTERPRISE"
                  : "ADDON"
              }
              badgeClass={badgeClass}
              enabled={subOn}
              enabledClass={
                badgeClass === "ai"
                  ? "module-card--enabled-ai"
                  : badgeClass === "enterprise"
                  ? "module-card--enabled-enterprise"
                  : "module-card--enabled-addon"
              }
              icon={getModuleIcon(sub.module_name)}
              title={sub.module_name}
              tagline={
                sub.module_description ||
                MODULE_TAGLINES[normalizeName(sub.module_name)]
              }
              isNew={normalizeName(sub.module_name) === "flexi ai"}
              description={sub.module_description}
              dependency={dependency}
              dependencyMet={dependencyMet}
              sectionLabel="KEY CAPABILITIES"
              chips={
                capabilities.length
                  ? capabilities.map((cap) => (
                      <span
                        key={cap}
                        className={`module-chip ${
                          subOn ? "module-chip--on" : "module-chip--off"
                        }`}
                      >
                        {cap}
                      </span>
                    ))
                  : null
              }
              footerLabel="MODULE NODE"
              toggleDisabled={!dependencyMet && !subOn}
              toggleTooltip={!dependencyMet && !subOn ? dependency : ""}
              onToggle={() => toggleSubModule(sub, parent)}
            />
          ),
        });
      });
    });

    if (activeCategory === "all") return cards;
    return cards.filter((card) => card.category === activeCategory);
  }, [
    hierarchy,
    parentEnabled,
    subEnabled,
    parentById,
    parentEnabledMap,
    subEnabledMap,
    activeCategory,
  ]);

  const topologyNodes = useMemo(() => {
    const core = [];
    const pipeline = [];
    const modules = [];
    const ai = [];
    const enterprise = [];

    hierarchy.forEach((parent) => {
      const parentKey = normalizeParentKey(parent.module_name);
      const parentOn = parentEnabled[parentKey] ?? false;
      const subs = extractSubModules(parent);
      const category = getParentCategory(parent.module_name);

      if (isCoreParent(parent.module_name)) {
        core.push({
          id: parentKey,
          title: `${getDisplayParentName(parent.module_name)} Host`,
          subtitle:
            parentKey === "pms" ? "Core Subscription" : "Core Gateway",
          icon: getModuleIcon(parent.module_name),
          active: parentOn,
          accent: "blue",
        });

        subs.forEach((sub) => {
          const subKey = normalizeKey(sub.module_name);
          pipeline.push({
            id: subKey,
            title: sub.module_name,
            subtitle: `Auto / ${getDisplayParentName(parent.module_name)} API`,
            icon: getModuleIcon(sub.module_name),
            active: (subEnabled[subKey] ?? false) && parentOn,
            accent: "blue",
          });
        });
        return;
      }

      subs.forEach((sub) => {
        const subKey = normalizeKey(sub.module_name);
        const node = {
          id: subKey,
          title: sub.module_name,
          subtitle:
            category === "ai"
              ? getDependencyLabel(sub, parentById) || "AI Module"
              : "PMS Bound",
          icon: getModuleIcon(sub.module_name),
          active: subEnabled[subKey] ?? false,
          accent: category === "ai" ? "purple" : "green",
        };

        if (category === "ai") ai.push(node);
        else if (normalizeName(sub.module_name) === "gha") enterprise.push(node);
        else if (normalizeName(sub.module_name) === "group dashboard") {
          enterprise.push({ ...node, accent: "amber" });
        } else modules.push(node);
      });
    });

    return { core, pipeline, modules, ai, enterprise };
  }, [hierarchy, parentEnabled, subEnabled]);

  const hasChanges = useMemo(() => {
    const parentChanged = Object.keys(parentEnabled).some(
      (key) => parentEnabled[key] !== initialParentEnabled[key]
    );
    const subChanged = Object.keys(subEnabled).some(
      (key) => subEnabled[key] !== initialSubEnabled[key]
    );
    return parentChanged || subChanged;
  }, [parentEnabled, subEnabled, initialParentEnabled, initialSubEnabled]);

  const handleSave = async () => {
    const parentChanged = Object.keys(parentEnabled).some(
      (key) => parentEnabled[key] !== initialParentEnabled[key]
    );
    const subChanged = Object.keys(subEnabled).some(
      (key) => subEnabled[key] !== initialSubEnabled[key]
    );

    if (!parentChanged && !subChanged) {
      toast.info("No changes to save");
      return;
    }

    const enabledParentModules = [];
    const disabledParentModules = [];

    Object.entries(parentEnabled).forEach(([key, enabled]) => {
      if (!CORE_PARENT_KEYS.has(key)) return;
      const parent = hierarchy.find(
        (item) => normalizeParentKey(item.module_name) === key
      );
      if (!parent) return;
      if (enabled) enabledParentModules.push(parent.module_name);
      else disabledParentModules.push(parent.module_name);
    });

    const enabledSubModuleIds = [];
    const disabledSubModuleIds = [];
    const enabledSubModuleNames = [];
    const disabledSubModuleNames = [];

    subModuleCatalog.forEach((subModule) => {
      const key = normalizeKey(subModule.module_name);
      const enabled = subEnabled[key] ?? false;
      const dependency = getDependencyLabel(subModule, parentById);
      const shouldBeEnabled =
        enabled && isDependencyMet(dependency, parentEnabledMap, subEnabledMap);

      if (shouldBeEnabled) {
        enabledSubModuleIds.push(subModule.id);
        enabledSubModuleNames.push(subModule.module_name);
      } else {
        disabledSubModuleIds.push(subModule.id);
        disabledSubModuleNames.push(subModule.module_name);
      }
    });

    setSaving(true);
    try {
      const requests = [];

      if (parentChanged) {
        requests.push(
          handleSpringDataMutationRequest(
            "POST",
            "core/api/v1/accesscontrol/update-hotel-parent-modules",
            {
              hotel_id: String(hotelId),
              enabled_parent_modules: enabledParentModules,
              disabled_parent_modules: disabledParentModules,
              policy_name: "Standard",
            }
          )
        );
      }

      if (subChanged) {
        requests.push(
          handleSpringDataMutationRequest(
            "POST",
            "core/api/v1/accesscontrol/update-hotel-sub-modules",
            {
              hotel_id: String(hotelId),
              enabled_sub_module_ids: enabledSubModuleIds,
              disabled_sub_module_ids: disabledSubModuleIds,
              enabled_sub_module_names: enabledSubModuleNames,
              disabled_sub_module_names: disabledSubModuleNames,
              policy_name: "Standard",
            }
          )
        );
      }

      await Promise.all(requests);
      toast.success("Modules updated successfully");
      setRefresh(new Date());
      fetchModules();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data ||
          err?.message ||
          "Failed to update modules"
      );
    } finally {
      setSaving(false);
    }
  };

  const totalPackages = useMemo(() => {
    let count = 0;
    hierarchy.forEach((parent) => {
      if (isCoreParent(parent.module_name)) count += 1;
      else count += extractSubModules(parent).length;
    });
    return count;
  }, [hierarchy]);

  if (fetching) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="module-registry">
      <div className="module-registry__tabs">
        {CATEGORIES.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className={`module-registry__tab ${
              activeCategory === id ? "module-registry__tab--active" : ""
            }`}
            onClick={() => setActiveCategory(id)}
          >
            <Icon sx={{ fontSize: 16 }} />
            {label}
          </button>
        ))}
      </div>

      <div className="module-registry__panel">
        <div className="module-registry__header">
          <div>
            <h2 className="module-registry__title">Module Registry</h2>
            <p className="module-registry__subtitle">
              Deploy, authorize, and configure live system nodes based on
              hardware allocation permissions.
            </p>
          </div>
          <span className="module-registry__count">
            Showing {displayCards.length} of {totalPackages} Total Packages
          </span>
        </div>

        {displayCards.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No modules found for this category.
          </Typography>
        ) : (
          <div className="module-registry__grid">
            {displayCards.map((card) => (
              <React.Fragment key={card.id}>{card.node}</React.Fragment>
            ))}
          </div>
        )}
      </div>

      <TopologyGraph nodes={topologyNodes} />

      <div className="module-registry__actions">
        <Button
          variant="custom-button"
          sx={{ width: "250px" }}
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          Save Changes {saving && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Button>
        {hasChanges && (
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>
            Unsaved changes — enable/disable modules then save.
          </Typography>
        )}
      </div>
    </Box>
  );
}
